/**
 * Team picker backed by server-side FTS5 search.
 *
 * The original loaded the whole library into a `logoLibraryCache` array (capped
 * at 200 rows for 168+ teams, so the tail was unreachable) and filtered it with
 * String.includes on every keystroke. Search now runs in SQLite, and results
 * are ordered by how often a team has actually been picked.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api, toTeamEntry } from '../lib/api';
import { onColor } from '../../shared/team-colors';
import type { TeamLibraryEntry, TeamKey } from '../../shared/types';
import { LEAGUES, LEAGUE_ORDER } from '../../shared/sports';

interface Props {
  side: TeamKey;
  onSelect: (team: TeamLibraryEntry) => void;
  onClose: () => void;
}

export function TeamPicker({ side, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [league, setLeague] = useState('');
  const [teams, setTeams] = useState<TeamLibraryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounced = useDebounced(query, 200);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api
      .searchTeams({ q: debounced, league })
      .then((res) => {
        if (controller.signal.aborted) return;
        setTeams(res.teams.map(toTeamEntry));
        setError(null);
      })
      .catch((err: Error) => {
        if (!controller.signal.aborted) setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [debounced, league]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Choose the ${side} team`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="modal">
        <div className="modal-head">
          <h2>Choose {side} team</h2>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="row" style={{ marginBottom: 12 }}>
          <input
            ref={inputRef}
            placeholder="Search teams…"
            aria-label="Search teams"
            value={query}
            style={{ flex: '2 1 180px' }}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            aria-label="League"
            value={league}
            style={{ flex: '1 1 110px' }}
            onChange={(e) => setLeague(e.target.value)}
          >
            <option value="">All leagues</option>
            {LEAGUE_ORDER.map((id) => (
              <option key={id} value={id}>
                {LEAGUES[id].label}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="small" style={{ color: 'var(--danger)' }}>{error}</p>}
        {loading && <p className="small muted">Searching…</p>}
        {!loading && teams.length === 0 && (
          <p className="empty-state">
            No teams found.
            {' Run a team sync from the admin panel if the library looks empty.'}
          </p>
        )}

        <div className="stack" style={{ gap: 4 }}>
          {teams.map((team) => (
            <button
              key={team.id}
              type="button"
              className="row btn-ghost"
              style={{ justifyContent: 'flex-start', textAlign: 'left', width: '100%' }}
              onClick={() => {
                // Fire-and-forget: ranking is a nicety, not worth blocking on.
                void api.markTeamUsed(team.id).catch(() => {});
                onSelect(team);
              }}
            >
              {team.logoUrl ? (
                <img src={team.logoUrl} alt="" width={28} height={28} style={{ objectFit: 'contain' }} />
              ) : (
                <span
                  aria-hidden="true"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: team.primaryColor ? onColor(team.primaryColor) : '#fff',
                    background: team.primaryColor || 'var(--surface-3)',
                  }}
                >
                  {team.abbr.slice(0, 3) || team.name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span>
                <strong>{team.name}</strong>{' '}
                <span className="muted small">{team.mascot}</span>
                <br />
                <span className="muted small">
                  {team.league} · {team.conference || 'Independent'}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return useMemo(() => debounced, [debounced]);
}
